import { vPopperThemes } from "@/config/vPopperThemes";
import { createApp } from "vue";

import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import { createPinia } from "pinia";

import sentryInit from "@/sentry-init";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import FloatingVue from "floating-vue";

import { applyDirectives, testIdDirective } from "@/directives";
import { exchangeTempTokenIfPresent } from "@/utils/exchangeTempToken";
import { sentryCaptureException } from "@/utils/error-handling/sentryCaptureException";
import { useAuthStore } from "@/stores/userAuth";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import App from "./App.vue";
import router from "./router";

// import styles
import "primevue/resources/themes/saga-blue/theme.css";
import "primevue/resources/primevue.min.css";
import "primeicons/primeicons.css";
import "floating-vue/dist/style.css";
import "./assets/styles/global.scss";

// When a new deployment invalidates old chunk hashes, Vite's preload helper
// fires this event before the promise rejection propagates. Preventing default
// stops the error from reaching Sentry, and we reload to fetch fresh assets.
// Guard against infinite reload loops (e.g. user offline, blocked assets).
const VITE_PRELOAD_RELOADED_KEY = "vitePreloadErrorReloaded";

window.addEventListener("vite:preloadError", (event) => {
    const lastReload = Number(
        sessionStorage.getItem(VITE_PRELOAD_RELOADED_KEY) || 0
    );
    const recentlyReloaded = Date.now() - lastReload < 10_000;

    if (recentlyReloaded || !navigator.onLine) return;

    event.preventDefault();
    sessionStorage.setItem(VITE_PRELOAD_RELOADED_KEY, String(Date.now()));
    window.location.reload();
});

const app = createApp(App);

sentryInit(app);

app.use(createPinia().use(piniaPluginPersistedstate));
app.use(PrimeVue);
app.use(ToastService);

app.use(FloatingVue, {
    themes: vPopperThemes,
});

applyDirectives(app, [testIdDirective]);

dayjs.extend(utc);

// Inbound SSO handshake from the new buyer app: if the URL carries
// `?temp_token=`, exchange it for a real auth token and pre-populate the auth
// store BEFORE installing the router. `app.use(router)` triggers Vue Router's
// initial navigation synchronously, so installing it before the exchange
// completes lets the router resolve the public `/signin` route against a
// still-empty auth store — the user lands on the login page even though their
// session is moments away from being valid. Installing the router only after
// auth has resolved guarantees the first beforeEach guard sees the final auth
// state and redirects appropriately.
//
// The whole bootstrap is bounded by a timeout: if the exchange or
// authenticate request hangs (slow backend, broken network), we still mount
// so the user can reach `/signin` instead of being stuck on a blank screen.
const BOOT_TIMEOUT_MS = 5000;

const withBootTimeout = <T>(promise: Promise<T>): Promise<T | void> =>
    new Promise<T | void>((resolve) => {
        const timer = setTimeout(() => {
            sentryCaptureException(
                new Error(`Bootstrap exceeded ${BOOT_TIMEOUT_MS}ms`)
            );
            resolve();
        }, BOOT_TIMEOUT_MS);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (err) => {
                clearTimeout(timer);
                sentryCaptureException(err);
                resolve();
            }
        );
    });

const bootstrap = async () => {
    try {
        await withBootTimeout(
            (async () => {
                await exchangeTempTokenIfPresent();
                const authStore = useAuthStore();
                if (authStore.token) {
                    await authStore.authenticateUser();
                }
            })()
        );
    } finally {
        app.use(router);
        app.mount("#app");
    }
};

bootstrap();
